import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Boton } from '#components/mobile/ui/Boton';
import { useEnableBankingStatus } from '#hooks/useEnableBankingStatus';
import { useSyncedPref } from '#hooks/useSyncedPref';

import { Bocadillo } from './Bocadillo';
import { leerFaros, marcarFaro } from './estado';
import type { Faro } from './estado';
import { Z_TRAVESIA } from './pasos/comun';

/**
 * Faro que toca en una ruta (Movimientos y Cuentas), o ninguno. El de
 * Cuentas habla de deslizar para sincronizar el banco: sin banco, no sale.
 */
export function faroDeRuta(pathname: string, conBanca: boolean): Faro | null {
  if (pathname === '/gastos') {
    return 'gastos';
  }
  if (pathname === '/accounts' && conBanca) {
    return 'cuentas';
  }
  return null;
}

/**
 * Los dos avisos de una línea tras la travesía: la primera vez que se abre
 * Movimientos y la primera vez que se abre Cuentas. Cada uno sale una sola
 * vez (pref sincronizada `travesia-faros`, marcada al enseñarlo).
 */
export function Faros() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [raw, setRaw] = useSyncedPref('travesia-faros');
  const [visible, setVisible] = useState<Faro | null>(null);
  const { bancaPermitida } = useEnableBankingStatus();

  const toca = faroDeRuta(pathname, bancaPermitida);
  useEffect(() => {
    if (!toca) {
      setVisible(null);
      return;
    }
    if (leerFaros(raw).includes(toca)) {
      return;
    }
    setVisible(toca);
    setRaw(marcarFaro(raw, toca));
    // Solo al cambiar de ruta: la pref marcada no debe quitarlo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toca]);

  if (!visible || visible !== toca) {
    return null;
  }
  const texto =
    visible === 'gastos'
      ? t(
          'Here you see every expense. To note one down: compass → Add transaction.',
        )
      : t('Pull down to sync your banks.');

  return createPortal(
    <div
      data-testid={`faro-${visible}`}
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 'calc(86px + env(safe-area-inset-bottom, 0px))',
        zIndex: Z_TRAVESIA.flota,
      }}
    >
      <Bocadillo texto={texto} estado="vuela">
        <div className="acciones">
          <Boton
            bloque
            data-testid="faro-entendido"
            style={{ minHeight: 46 }}
            onPress={() => setVisible(null)}
          >
            <Trans>Got it</Trans>
          </Boton>
        </div>
      </Bocadillo>
    </div>,
    document.body,
  );
}
